import React, { useState } from 'react';
import { Lock, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { THEMES, getColorClasses } from '../pages/themeConfig';
import { 
  researchTree,
  getResearchById,
  isResearchAvailable,
  getCategoryConfig 
} from './research/researchTree';

const ResearchLab = ({ resources, onStartResearch, currentResearch, completedResearch = [], theme = 'vanilla' }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedTiers, setExpandedTiers] = useState({
    early: true,
    mid: true,
    late: false
  });
  const config = THEMES[theme] || THEMES.vanilla;

  // Helper function to get themed project name/description
  const getThemedProject = (project) => {
    if (!project) return project;
    const override = config.research?.projectOverrides?.[project.id];
    return {
      ...project,
      name: override?.name || project.name,
      description: override?.description || project.description
    };
  };

  const toggleTier = (tier) => {
    setExpandedTiers(prev => ({
      ...prev,
      [tier]: !prev[tier]
    }));
  };

  const getProjectStatus = (project) => {
    // Check if project.id is in completedResearch array
    // Handle both string IDs and object references
    const isCompleted = completedResearch.some(completed => 
      typeof completed === 'string' ? completed === project.id : completed.id === project.id
    );
    
    if (isCompleted) {
      return 'completed';
    }
    if (currentResearch?.id === project.id) {
      return 'researching';
    }
    if (!isResearchAvailable(project.id, completedResearch)) {
      return 'locked';
    }
    return 'available';
  };

  const canAffordResearch = (project) => {
    return (
      resources.funds >= project.cost.funds &&
      resources.supplies >= project.cost.supplies &&
      resources.intel >= project.cost.intel
    );
  };

  const handleStartResearch = (project) => {
    const status = getProjectStatus(project);
    if (status === 'locked' || status === 'completed') return;
    if (!canAffordResearch(project)) return;
    
    onStartResearch(project);
    setSelectedProject(null);
  };

  const getTierInfo = (tier) => {
    const tierConfig = {
      early: {
        title: config.research?.earlyTier || 'TIER 1: FOUNDATION',
        description: config.research?.earlyDesc || 'Basic technologies to establish your operations',
        color: 'text-green-400'
      },
      mid: {
        title: config.research?.midTier || 'TIER 2: SPECIALIZATION',
        description: config.research?.midDesc || 'Advanced systems and tactical improvements',
        color: 'text-blue-400'
      },
      late: {
        title: config.research?.lateTier || 'TIER 3: CUTTING EDGE',
        description: config.research?.lateDesc || 'Experimental technology and ultimate power',
        color: 'text-purple-400'
      }
    };
    return tierConfig[tier] || tierConfig.early;
  };

  const renderTierSection = (tier) => {
    const tierInfo = getTierInfo(tier);
    const projects = researchTree[tier];
    const isExpanded = expandedTiers[tier];
    
    const tierStats = {
      total: projects.length,
      completed: projects.filter(p => {
        return completedResearch.some(completed => 
          typeof completed === 'string' ? completed === p.id : completed.id === p.id
        );
      }).length,
      available: projects.filter(p => getProjectStatus(p) === 'available').length
    };

    return (
      <div key={tier} className="mb-6">
        <button
          onClick={() => toggleTier(tier)}
          className="w-full flex items-center justify-between bg-gray-800 border-2 border-gray-700 hover:border-gray-600 rounded-lg p-4 mb-3 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className={`${tierInfo.color} font-bold text-lg`}>
              {tierInfo.title}
            </div>
            <div className="text-sm text-gray-400">
              {tierInfo.description}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-500">
              <span className="text-green-400 font-bold">{tierStats.completed}</span>
              {' / '}
              <span className="text-gray-300">{tierStats.total}</span>
              {' '}{config.research?.completedLabel || 'completed'}
            </div>
            {tierStats.available > 0 && (
              <div className={`px-2 py-1 rounded ${getColorClasses(config.colors.accent, 'bg')} text-xs font-bold`}>
                {tierStats.available} {config.research?.availableLabel || 'Available'}
              </div>
            )}
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>

        {isExpanded && (
          <div className="grid grid-cols-2 gap-4">
            {projects.map(project => {
              const themedProject = getThemedProject(project);
              const Icon = project.icon;
              const status = getProjectStatus(project);
              const canAfford = canAffordResearch(project);
              const isSelected = selectedProject?.id === project.id;
              const categoryConfig = getCategoryConfig(project.category);

              return (
                <button
                  key={project.id}
                  onClick={() => status !== 'completed' && setSelectedProject(themedProject)}
                  disabled={status === 'completed'}
                  className={`text-left bg-gray-800 border-2 rounded-lg p-6 transition-all ${
                    status === 'completed'
                      ? 'border-gray-700 opacity-50 cursor-default grayscale'
                      : status === 'locked' 
                      ? 'border-gray-700 opacity-50 cursor-not-allowed' 
                      : status === 'researching'
                      ? `${getColorClasses(config.colors.accent, 'border')} bg-gray-750 animate-pulse`
                      : isSelected
                      ? `${getColorClasses(config.colors.accent, 'border')} bg-gray-750`
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`${categoryConfig.bgClass} ${categoryConfig.borderClass} ${categoryConfig.textClass} p-3 rounded-lg border`}>
                      <Icon size={24} />
                    </div>
                    <div className="flex gap-2">
                      {status === 'locked' && (
                        <Lock className="text-gray-500" size={20} />
                      )}
                      {status === 'researching' && (
                        <Clock className={getColorClasses(config.colors.accent, 'text')} size={20} />
                      )}
                      {status === 'completed' && (
                        <CheckCircle className="text-green-400" size={20} />
                      )}
                    </div>
                  </div>

                  <h3 className="text-lg font-bold mb-2">{themedProject.name}</h3>
                  <p className="text-sm text-gray-400 mb-4">{themedProject.description}</p>

                  {status === 'locked' && project.prerequisites.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-red-400 font-bold mb-1">
                        {config.research?.prerequisitesLabel || 'PREREQUISITES'}:
                      </p>
                      {project.prerequisites.map((prereqId, idx) => {
                        const prereq = getResearchById(prereqId);
                        const themedPrereq = getThemedProject(prereq);
                        const isCompleted = completedResearch.some(completed => 
                          typeof completed === 'string' ? completed === prereqId : completed.id === prereqId
                        );
                        return (
                          <p key={idx} className={`text-xs ${isCompleted ? 'text-green-400 line-through' : 'text-gray-500'}`}>
                            • {themedPrereq?.name || prereqId}
                          </p>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex gap-3 mb-4 text-xs flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400">💰</span>
                      <span className={status === 'available' && canAfford ? 'text-green-400' : status === 'available' ? 'text-red-400' : 'text-gray-500'}>
                        ${project.cost.funds.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400">📦</span>
                      <span className={status === 'available' && resources.supplies >= project.cost.supplies ? 'text-green-400' : status === 'available' ? 'text-red-400' : 'text-gray-500'}>
                        {project.cost.supplies}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-400">📡</span>
                      <span className={status === 'available' && resources.intel >= project.cost.intel ? 'text-green-400' : status === 'available' ? 'text-red-400' : 'text-gray-500'}>
                        {project.cost.intel}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="text-gray-400" size={12} />
                      <span className="text-gray-300">{project.duration}d</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-700 pt-3">
                    <p className="text-xs text-gray-500 mb-2">
                      {config.research?.benefitsLabel || 'BENEFITS'}:
                    </p>
                    {project.benefits.slice(0, 3).map((benefit, idx) => (
                      <p key={idx} className="text-xs text-green-400">✓ {benefit}</p>
                    ))}
                    {project.benefits.length > 3 && (
                      <p className="text-xs text-gray-500 mt-1">+{project.benefits.length - 3} more...</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">{config.research?.title || 'Research & Development'}</h2>
          <p className="text-gray-400 text-sm mt-1">
            {config.research?.subtitle || 'Unlock new technologies and enhance your capabilities'}
          </p>
        </div>
        {currentResearch && (
          <div className={`${getColorClasses(config.colors.accent, 'bg')} border ${getColorClasses(config.colors.accent, 'border')} rounded-lg px-4 py-3 min-w-[320px]`}>
            <div className="flex items-center gap-3">
              <Clock className={getColorClasses(config.colors.accent, 'text')} size={16} />
              <div className="flex-1">
                <p className="text-xs text-gray-400">{config.research?.researchingLabel || 'RESEARCHING'}</p>
                <p className={`font-bold ${getColorClasses(config.colors.accent, 'text')}`}>
                  {getThemedProject(currentResearch).name}
                </p>
                
                {/* Progress Bar */}
                <div className="mt-2 bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div 
                    className={`h-full ${getColorClasses(config.colors.accent, 'bg')} transition-all duration-500`}
                    style={{ width: `${(currentResearch.progress || 0)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {Math.round(currentResearch.progress || 0)}% • {Math.max(0, Math.ceil(currentResearch.duration * (1 - (currentResearch.progress || 0) / 100)))} days remaining
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Research Progress Overview */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">{config.research?.totalCompletedLabel || 'Total Completed'}</p>
          <p className="text-2xl font-bold text-green-400">{completedResearch.length}</p>
          <p className="text-xs text-gray-500">{config.research?.ofLabel || 'of'} {researchTree.early.length + researchTree.mid.length + researchTree.late.length} {config.research?.projectsLabel || 'projects'}</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">{config.research?.currentResearchLabel || 'Current Research'}</p>
          <p className="text-2xl font-bold text-blue-400">{currentResearch ? '1' : '0'}</p>
          <p className="text-xs text-gray-500">{currentResearch ? getThemedProject(currentResearch).name : (config.research?.noneActiveLabel || 'None active')}</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">{config.research?.availableProjectsLabel || 'Available Projects'}</p>
          <p className={`text-2xl font-bold ${getColorClasses(config.colors.accent, 'text')}`}>
            {[...researchTree.early, ...researchTree.mid, ...researchTree.late]
              .filter(p => getProjectStatus(p) === 'available').length}
          </p>
          <p className="text-xs text-gray-500">{config.research?.readyToResearchLabel || 'Ready to research'}</p>
        </div>
      </div>

      {/* Research Tiers */}
      {renderTierSection('early')}
      {renderTierSection('mid')}
      {renderTierSection('late')}

      {/* Selected Project Detail */}
      {selectedProject && (
        <div className={`fixed bottom-6 left-6 right-6 bg-gray-900 border-2 ${getColorClasses(config.colors.accent, 'border')} rounded-lg p-6 shadow-2xl z-50`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-2xl font-bold">{selectedProject.name}</h3>
                <span className={`px-2 py-1 rounded text-xs font-bold ${getCategoryConfig(selectedProject.category).bgClass} ${getCategoryConfig(selectedProject.category).textClass}`}>
                  {selectedProject.category.toUpperCase()}
                </span>
              </div>
              <p className="text-gray-400 mb-4">{selectedProject.description}</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-2 font-bold">{config.research?.benefitsLabel || 'BENEFITS'}:</p>
                  {selectedProject.benefits.map((benefit, idx) => (
                    <p key={idx} className="text-sm text-green-400 mb-1">✓ {benefit}</p>
                  ))}
                </div>
                {selectedProject.unlocks && selectedProject.unlocks.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2 font-bold">{config.research?.unlocksLabel || 'UNLOCKS'}:</p>
                    {selectedProject.unlocks.map((unlockId, idx) => {
                      const unlock = getResearchById(unlockId);
                      const themedUnlock = getThemedProject(unlock);
                      return (
                        <p key={idx} className="text-sm text-purple-400 mb-1">→ {themedUnlock?.name || unlockId}</p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <button 
              onClick={() => setSelectedProject(null)}
              className="text-gray-400 hover:text-white ml-4"
            >
              ✕
            </button>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => handleStartResearch(selectedProject)}
              disabled={!canAffordResearch(selectedProject) || getProjectStatus(selectedProject) !== 'available'}
              className={`px-8 py-3 rounded-lg font-bold text-lg ${
                canAffordResearch(selectedProject) && getProjectStatus(selectedProject) === 'available'
                  ? `${getColorClasses(config.colors.accent, 'bgMain')} ${getColorClasses(config.colors.accent, 'bgHover')} text-white`
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {getProjectStatus(selectedProject) === 'available' && canAffordResearch(selectedProject)
                ? (config.research?.startButton || 'START RESEARCH')
                : getProjectStatus(selectedProject) === 'available'
                ? (config.research?.insufficientButton || 'INSUFFICIENT RESOURCES')
                : (config.research?.notAvailableLabel || 'NOT AVAILABLE')}
            </button>
            <button
              onClick={() => setSelectedProject(null)}
              className="bg-gray-700 hover:bg-gray-600 px-6 py-3 rounded-lg font-bold"
            >
              {config.research?.cancelButton || 'CANCEL'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchLab;